import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
});

export const api = {
  health: () => http.get("/health").then((r) => r.data),

  // ---- Tasks ----
  listTasks: (clientId) =>
    http
      .get("/tasks", { params: clientId ? { clientId } : {} })
      .then((r) => r.data),
  createTask: (payload) => http.post("/tasks", payload).then((r) => r.data),
  updateTask: (id, payload) =>
    http.put(`/tasks/${id}`, payload).then((r) => r.data),
  deleteTask: (id) => http.delete(`/tasks/${id}`).then((r) => r.data),
  bulkDeleteTasks: (ids) =>
    http.post("/tasks/bulk-delete", { ids }).then((r) => r.data),
  uploadDocument: (id, file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return http
      .post(`/tasks/${id}/document`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) {
            onProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      })
      .then((r) => r.data);
  },
  deleteDocument: (id) =>
    http.delete(`/tasks/${id}/document`).then((r) => r.data),
  documentUrl: (id) => {
    const t = localStorage.getItem("ndm.auth.token") || "";
    const q = t ? `?token=${encodeURIComponent(t)}` : "";
    return `${API_BASE}/tasks/${id}/document${q}`;
  },

  // ---- Templates ----
  listTemplates: () => http.get("/templates").then((r) => r.data),
  createTemplate: (payload) =>
    http.post("/templates", payload).then((r) => r.data),
  updateTemplate: (id, payload) =>
    http.put(`/templates/${id}`, payload).then((r) => r.data),
  deleteTemplate: (id) => http.delete(`/templates/${id}`).then((r) => r.data),
  generateFromTemplate: (id, payload) =>
    http.post(`/templates/${id}/generate`, payload).then((r) => r.data),

  // ---- Clients ----
  listClients: () => http.get("/clients").then((r) => r.data),
  getClient: (id) => http.get(`/clients/${id}`).then((r) => r.data),
  createClient: (payload) => http.post("/clients", payload).then((r) => r.data),
  updateClient: (id, payload) =>
    http.put(`/clients/${id}`, payload).then((r) => r.data),
  deleteClient: (id) => http.delete(`/clients/${id}`).then((r) => r.data),
  bulkDeleteClients: (ids) =>
    http.post("/clients/bulk-delete", { ids }).then((r) => r.data),
  applyPack: (id, months = 12) =>
    http
      .post(`/clients/${id}/apply-pack`, null, { params: { months } })
      .then((r) => r.data),
  importClientsTemplateUrl: () => {
    const t = localStorage.getItem("ndm.auth.token") || "";
    const q = t ? `?token=${encodeURIComponent(t)}` : "";
    return `${API_BASE}/clients/import/template${q}`;
  },
  importClients: ({ file, generateCompliances, generationMonths, skipDuplicates }) => {
    const form = new FormData();
    form.append("file", file);
    return http
      .post("/clients/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: {
          generateCompliances,
          generationMonths,
          skipDuplicates,
        },
      })
      .then((r) => r.data);
  },

  // ---- Library ----
  library: () => http.get("/compliance-library").then((r) => r.data),

  // ---- Settings & reminders ----
  getNotifications: () =>
    http.get("/settings/notifications").then((r) => r.data),
  setNotifications: (payload) =>
    http.put("/settings/notifications", payload).then((r) => r.data),
  sendTestEmail: (to) =>
    http.post("/reminders/test-email", { to }).then((r) => r.data),
  runReminders: (payload = {}) =>
    http.post("/reminders/run-now", payload).then((r) => r.data),
  previewReminders: (targetDate) =>
    http
      .get("/reminders/preview", { params: targetDate ? { targetDate } : {} })
      .then((r) => r.data),
  reminderHistory: () => http.get("/reminders/history").then((r) => r.data),

  // ---- Master compliance library ----
  masterCompliances: () =>
    http.get("/master-compliances").then((r) => r.data),
  masterMatrix: () =>
    http.get("/master-compliances/matrix").then((r) => r.data),
  getClientRules: (id) =>
    http.get(`/clients/${id}/rules`).then((r) => r.data),
  updateClientRules: (id, changes) =>
    http
      .put(`/clients/${id}/rules`, { changes })
      .then((r) => r.data),
  regenerateClientRules: (id, payload) =>
    http
      .post(`/clients/${id}/rules/regenerate`, payload)
      .then((r) => r.data),
  // ---- Auth & users ----
  changePassword: (currentPassword, newPassword) =>
    http.post("/auth/change-password", { currentPassword, newPassword }).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  listStaff: () => http.get("/staff").then((r) => r.data),
  createStaff: (payload) => http.post("/staff", payload).then((r) => r.data),
  updateStaff: (id, payload) => http.put(`/staff/${id}`, payload).then((r) => r.data),
  deleteStaff: (id) => http.delete(`/staff/${id}`).then((r) => r.data),
  assignClient: (clientId, staffId) =>
    http.post(`/clients/${clientId}/assign`, { staffId }).then((r) => r.data),

  // ---- Reports ----
  reportsOverview: () => http.get("/reports/overview").then((r) => r.data),

  // ---- Public (landing page) ----
  publicNews: () => http.get("/public/news").then((r) => r.data),
  publicLandingCms: () => http.get("/public/cms/landing").then((r) => r.data),
  publicBlogs: (params = {}) =>
    http.get("/public/cms/blogs", { params }).then((r) => r.data),
  publicBlogDetail: (slug) =>
    http.get(`/public/cms/blogs/${slug}`).then((r) => r.data),
  publicCaseLaws: (params = {}) =>
    http.get("/public/cms/case-laws", { params }).then((r) => r.data),
  publicActs: (params = {}) =>
    http.get("/public/cms/acts", { params }).then((r) => r.data),
  publicCirculars: (params = {}) =>
    http.get("/public/cms/circulars", { params }).then((r) => r.data),
  publicAlerts: (params = {}) =>
    http.get("/public/cms/alerts", { params }).then((r) => r.data),

  // ---- CMS (admin only) ----
  cmsSummary: () => http.get("/cms/summary").then((r) => r.data),
  cmsList: (params) => http.get("/cms/content", { params }).then((r) => r.data),
  cmsGet: (id) => http.get(`/cms/content/${id}`).then((r) => r.data),
  cmsCreate: (payload) => http.post("/cms/content", payload).then((r) => r.data),
  cmsUpdate: (id, payload) =>
    http.put(`/cms/content/${id}`, payload).then((r) => r.data),
  cmsTogglePublish: (id) =>
    http.post(`/cms/content/${id}/toggle-publish`).then((r) => r.data),
  cmsDelete: (id) => http.delete(`/cms/content/${id}`).then((r) => r.data),
};
